import piImg from "@/assets/cs-personal-injury.jpg";
import duiImg from "@/assets/cs-dui.jpg";
import familyImg from "@/assets/cs-family.jpg";
import estateImg from "@/assets/cs-estate.jpg";
import proofPiAnalytics from "@/assets/proof-pi-analytics.jpg";
import proofPiSerp from "@/assets/proof-pi-serp.jpg";
import proofPiGbp from "@/assets/proof-pi-gbp.jpg";
import proofDuiGbp from "@/assets/proof-dui-gbp.jpg";
import proofDuiSerp from "@/assets/proof-dui-serp.jpg";
import proofDuiAnalytics from "@/assets/proof-dui-analytics.jpg";
import proofFamilyAnalytics from "@/assets/proof-family-analytics.jpg";
import proofFamilySerp from "@/assets/proof-family-serp.jpg";
import proofFamilyGbp from "@/assets/proof-family-gbp.jpg";
import proofEstateAnalytics from "@/assets/proof-estate-analytics.jpg";
import proofEstateSerp from "@/assets/proof-estate-serp.jpg";
import proofEstateGbp from "@/assets/proof-estate-gbp.jpg";

export type CaseStudy = {
  slug: string;
  practice: string;
  market: string;
  headline: string;
  shortHeadline: string;
  cover: string;
  metrics: [string, string][];
  challenge: string;
  strategy: string[];
  results: string;
  quote: { text: string; author: string; role: string };
  proofImages: { src: string; alt: string; caption: string }[];
  timeline: string;
  practiceHref: string;
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "personal-injury-houston",
    practice: "Personal Injury",
    market: "Houston, TX",
    headline: "From page 4 to #1 for 'car accident lawyer Houston' in 11 months.",
    shortHeadline: "9-attorney PI firm 3x'd organic consultations in under a year.",
    cover: piImg,
    metrics: [
      ["+312%", "Organic consultations"],
      ["$2.8M", "Attributed recovered fees"],
      ["27", "Top-3 keywords won"],
    ],
    challenge:
      "A 9-attorney personal injury firm in Houston was steadily losing market share to a national lead-gen brand and two well-funded local competitors. Their site was buried on page 4 for every commercial term that actually converts (car accident lawyer, 18 wheeler accident lawyer, motorcycle accident attorney). Paid spend was climbing, cost per signed case was up 42% year over year, and referral volume was flat.",
    strategy: [
      "Rebuilt the site's information architecture around case types instead of attorney bios",
      "Shipped 64 city + suburb + case-type landing pages (Katy, Sugar Land, The Woodlands, Pasadena, etc.)",
      "Earned 38 editorial links from regional news outlets covering local accident data",
      "Overhauled Google Business Profile with weekly posts, service categories, and 200+ verified reviews",
      "Deployed Attorney and LegalService schema plus case-result rich snippets across every page",
    ],
    results:
      "Within 6 months the local map pack flipped for 'car accident lawyer Houston' and 'personal injury attorney Houston'. By month 11 the firm ranked #1 organic for the primary money term and top-3 for 27 city + practice combinations. Organic consultations grew 312%, paid spend was cut in half without losing volume, and attributed recovered fees from organic traffic exceeded $2.8M in the first 12 months.",
    quote: {
      text: "We tripled our organic consultations in 9 months. The team understands personal injury economics, not just SEO.",
      author: "Managing Partner",
      role: "9-attorney PI Firm, Houston TX",
    },
    proofImages: [
      { src: proofPiAnalytics, alt: "Google Analytics 4 dashboard showing 312% organic traffic growth for Houston personal injury firm", caption: "Organic sessions grew from 1,847/mo to 7,610/mo over 11 months (Google Analytics 4)." },
      { src: proofPiSerp, alt: "Google SERP showing #1 organic ranking for car accident lawyer Houston", caption: "#1 organic and dominant local pack for 'car accident lawyer Houston', 33,100 monthly searches." },
      { src: proofPiGbp, alt: "Google Business Profile insights showing surge in calls and direction requests", caption: "GBP calls up 287% and direction requests increased after local optimization." },
    ],
    timeline: "11 months",
    practiceHref: "/practice-areas/personal-injury-lawyer-seo",
  },
  {
    slug: "dui-defense-phoenix",
    practice: "DUI Defense",
    market: "Phoenix, AZ",
    headline: "After-hours mobile calls grew 417% with a DUI-specific local strategy.",
    shortHeadline: "Solo DUI attorney cut paid spend 60% while doubling case volume.",
    cover: duiImg,
    metrics: [
      ["#1", "Local pack 'DUI lawyer Phoenix'"],
      ["417%", "After-hours mobile calls"],
      ["82%", "Of new leads from organic"],
    ],
    challenge:
      "A solo DUI defense attorney in Phoenix was spending $14,000/month on Google Ads with declining ROAS. Two aggregator sites and one national brand owned the top three positions for every DUI query in the market. Mugshot and arrest-record sites were ranking for his own name, hurting trust and killing conversion on the site he did have.",
    strategy: [
      "Built dedicated pages for first-offense DUI, felony DUI, refusal / implied consent, CDL DUI, and extreme DUI",
      "Optimized Google Business Profile for after-hours intake with 24/7 messaging and category tuning",
      "Ran a brand-protection campaign to push mugshot and arrest-record pages off page 1 for the attorney's name",
      "Published a monthly DUI case-result page with structured data to compound topical authority",
      "Earned links from Arizona bar-association resources and local recovery / addiction non-profits",
    ],
    results:
      "Within 5 months the firm owned the #1 local pack position for 'DUI lawyer Phoenix' and top-3 for every modifier query. After-hours mobile calls, the most valuable DUI lead type, grew 417%. Paid spend dropped 60% while total case volume rose. Organic now drives 82% of new leads. The attorney has stopped taking calls from Google Ads reps.",
    quote: {
      text: "Our DUI practice went from invisible to #1 in the local pack. The reporting alone is worth what we pay.",
      author: "Partner",
      role: "Criminal Defense Firm, Phoenix AZ",
    },
    proofImages: [
      { src: proofDuiGbp, alt: "Google Business Profile insights showing after-hours DUI call spike between 6pm and 2am", caption: "After-hours mobile calls (6pm–2am) grew from 12/week to 62/week, the exact window DUI arrests happen." },
      { src: proofDuiSerp, alt: "Google local pack #1 ranking for DUI lawyer Phoenix", caption: "#1 in the local pack for 'DUI lawyer Phoenix' and every high-intent modifier." },
      { src: proofDuiAnalytics, alt: "Lead source attribution dashboard showing 82% of leads coming from organic search", caption: "Attribution model: 82% of signed DUI cases now originate from organic search, up from 19%." },
    ],
    timeline: "9 months",
    practiceHref: "/practice-areas/dui-dwi-lawyer-seo",
  },
  {
    slug: "family-law-chicago",
    practice: "Family Law",
    market: "Chicago, IL",
    headline: "248% growth in divorce consultations across three Illinois counties.",
    shortHeadline: "Family law firm expanded from Cook County into DuPage and Lake, top-3 in all three.",
    cover: familyImg,
    metrics: [
      ["+248%", "Organic consults"],
      ["37", "Page-1 county+practice keywords"],
      ["4.2x", "Organic ROI vs paid"],
    ],
    challenge:
      "A 6-attorney family law firm in Chicago wanted to expand from Cook County into DuPage and Lake counties. Their existing site had one generic 'divorce' page and one generic 'child custody' page, no county-level targeting, no attorney schema, no case results, no lead magnet. They were invisible outside their home ZIP code.",
    strategy: [
      "Built county-specific hub pages for Cook, DuPage, and Lake with distinct case-value context and local courthouse info",
      "Rewrote all attorney bios with proper Person schema, bar admissions, education, and case-type expertise",
      "Launched a downloadable divorce checklist as a top-of-funnel lead magnet gated behind email capture",
      "Published 40+ practice-area pages covering contested divorce, uncontested, high-asset, custody, support, and post-decree",
      "Ran a review-generation campaign across three GBP listings",
    ],
    results:
      "All three counties now rank top-3 for 'divorce lawyer [county]' and top-5 for 'family law attorney [county]'. Consultation volume grew 248% year over year. Cost per signed case from organic came in at $187 vs $782 from paid, a 4.2x organic ROI. The firm added two associates on the strength of the pipeline.",
    quote: {
      text: "Finally an SEO agency that doesn't pretend the algorithm changes every Tuesday. Honest, focused, effective.",
      author: "Founding Partner",
      role: "Family Law Firm, Chicago IL",
    },
    proofImages: [
      { src: proofFamilyAnalytics, alt: "GA4 conversions dashboard segmented by Cook, DuPage, and Lake counties", caption: "Organic consultation form fills grew from 34/mo to 118/mo across three counties." },
      { src: proofFamilySerp, alt: "Google Search Console performance report showing top-3 rankings across Cook, DuPage, and Lake counties", caption: "Top-3 for 'divorce lawyer' in all three target counties within 8 months." },
      { src: proofFamilyGbp, alt: "Google Business Profile Manager showing three county location listings for a family law firm", caption: "Three separate GBP listings, each ranking in the top 3 of the local pack for their county." },
    ],
    timeline: "12 months",
    practiceHref: "/practice-areas/family-law-lawyer-seo",
  },
  {
    slug: "estate-planning-boston",
    practice: "Estate Planning",
    market: "Boston, MA",
    headline: "Wills, trusts, and probate inquiries up 184% in 9 months.",
    shortHeadline: "Traditional estate firm rebuilt around high-complexity queries LegalZoom can't touch.",
    cover: estateImg,
    metrics: [
      ["+184%", "Estate consults"],
      ["3.1x", "Trust formation inquiries"],
      ["+62%", "Engagement vs benchmark"],
    ],
    challenge:
      "A traditional Boston estate planning firm with an outdated site could not compete with LegalZoom, Trust & Will, and Rocket Lawyer on broad terms like 'online will' or 'simple trust'. Their true expertise, irrevocable trusts, Medicaid asset protection, complex probate, was buried under generic content aimed at the wrong audience.",
    strategy: [
      "Pivoted content strategy away from DIY-competitive terms toward high-complexity local queries",
      "Published deep pillar pages on irrevocable trusts, Medicaid planning, special needs trusts, and estate tax planning",
      "Built a probate administration hub tied to Suffolk, Middlesex, and Norfolk county probate courts",
      "Launched a monthly attorney-authored newsletter to build E-E-A-T signals and returning visitor share",
      "Added JSON-LD Attorney and LegalService schema plus FAQ schema on every service page",
    ],
    results:
      "Estate planning consultations grew 184% in 9 months. Trust formation inquiries, the highest-value engagement type, grew 3.1x. Average time on page rose 62% above the industry benchmark. The firm now ranks #1 for 'Medicaid asset protection attorney Boston' and top-3 for 14 other complex-planning terms LegalZoom will never compete for.",
    quote: {
      text: "PageOne understood that our value is complexity, not templates. They stopped chasing terms we could never win and rebuilt around what we actually do best.",
      author: "Senior Partner",
      role: "Estate Planning Firm, Boston MA",
    },
    proofImages: [
      { src: proofEstateAnalytics, alt: "GA4 dashboard showing 184% growth in estate consultation form submissions", caption: "Consultation form submissions grew from 22/mo to 62/mo in 9 months." },
      { src: proofEstateSerp, alt: "Google Search Console showing #1 ranking for Medicaid asset protection attorney Boston", caption: "#1 organic for 'Medicaid asset protection attorney Boston', a $12,000+ average matter value." },
      { src: proofEstateGbp, alt: "Google Business Profile Insights showing 214% growth in discovery searches", caption: "Discovery searches (people finding the firm without knowing the name) grew 214%." },
    ],
    timeline: "9 months",
    practiceHref: "/practice-areas/estate-planning-lawyer-seo",
  },
];

export const getCaseStudy = (slug: string) => caseStudies.find((c) => c.slug === slug);